import { RJSFSchema } from "@rjsf/utils";
import { GenericForm } from "../rjsf/GenericForm";
import { useState, useMemo } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import { FormDataProvider, useFormData } from "../context/FormDataContext";
import CreatorLabel from "./CreatorLabel";

const postExerciseSchema: RJSFSchema = {
  type: "object",
  properties: {
    target_joints: {
      type: "array",
      title: "Target Joints",
      items: {
        type: "string",
        enum: [
          "chest",
          "elbow",
          "fall_prevention",
          "foot",
          "hand",
          "hip",
          "jaw",
          "knee",
          "lower_back",
          "lower_leg",
          "neck",
          "neuro",
          "osteoporosis",
          "pelvic_floor",
          "shoulder",
          "upper_back",
          "wrist",
        ],
      },
      uniqueItems: true,
    },
    primary_function: {
      type: "string",
      title: "Primary Function",
      enum: ["strength", "mobility", "balance"],
      default: "strength",
    },
    premium: { type: "boolean", title: "Premium" },

    levels: {
      type: "array",
      minItems: 1,
      items: {
        type: "object",
        properties: {
          position: {
            type: "string",
            title: "Position",
            enum: ["none", "standing", "sitting", "lying"],
            default: "none",
          },
          movement: {
            type: "string",
            title: "Movement",
            enum: [
              "none",
              "flexion",
              "extension",
              "abduction",
              "adduction",
              "internal_rotation",
              "external_rotation",
              "lateral_flexion",
            ],
            default: "none",
          },
          duration_unit: {
            type: ["string"],
            title: "Duration Unit",
            enum: ["repetitions", "steps", "repetitions_twice", "seconds", "minutes"],
            default: "repetitions",
          },
          equipment: {
            type: "array",
            title: "Equipment",
            items: {
              type: "string",

              enum: [
                "resistance_band",
                "backpack",
                "stair",
                "towel",
                "exercise_ball",
                "water_bottles",
                "stick",
                "weight",
                "foamroller",

                "pool",
                "mobility_aid",
                "sling",
                "chair",
              ],
            },
            uniqueItems: true,
          },
          reps: { type: "string", title: "Reps", default: "15" },
          sets: { type: "number", title: "Sets", default: 1, minimum: 1 },

          translations: {
            type: "array",
            title: "Translation",
            minItems: 1,
            items: { $ref: "#/definitions/translation" },
            uniqueItems: true,
          },
        },
        required: ["reps", "sets"],
      },
    },
  },

  required: ["primary_function", "target_joints"],
  definitions: {
    translation: {
      type: "object",
      properties: {
        locale: {
          type: "string",
          title: "Locale",
          enum: ["sv-se", "es-us", "en-se", "en-gb", "en-us", "en-fr", "de-de", "en-de", "fr-fr"],
        },
        title: { type: "string", title: "Title" },
        variation_title: { type: "string", title: "Variation Title" },
        instructions_text1: { type: "string", title: "Instructions Text 1" },
        instructions_text3: {
          type: "string",
          title:
            "instructions_text3: LEGACY, DO NOT USE. Specify number in sets in sets field and general instructions in instructions_text1",
        },
        about: { type: "string", title: "About" },
        tip_description: { type: "string", title: "Tip Description" },
        tip_title: { type: "string", title: "Tip Title" },
        // extra_swedish_description: { type: "string", title: "Extra Swedish Description" },
        caption_file_url: { type: "string", title: "Caption File URL" },
        image_url: { type: "string", title: "Image URL" },
        video: {
          type: "object",
          title: "Video",
          properties: {
            video_file_url: { type: "string", title: "Video File URL" },
            video_thumbnail_file_url: { type: "string", title: "Video Thumbnail File URL" },
          },
        },
      },
      required: ["title", "locale"],
    },
  },
};

const getPostExerciseUISchema = (isApproved: boolean, submitButtonText?: string): RJSFSchema => ({
  "ui:submitButtonOptions": {
    submitText: submitButtonText || "Submit",
  },
  primary_function: {
    "ui:widget": "radio",
    "ui:options": {
      inline: true,
    },
  },
  target_joints: {
    "ui:widget": "checkboxes",
    "ui:options": {
      inline: true,
    },
  },
  levels: {
    "ui:options": {
      addLabel: "Add Level",
      orderable: !isApproved,
      addable: !isApproved,
      removable: !isApproved,
    },
    items: {
      type: "object",
      "ui:options": {
        collapsed: false,
        collapsible: true,
        titleTemplate:
          "#{level} {translations['en-us'].title} {translations['en-us'].variation_title} ({translations['sv-se'].title} {translations['sv-se'].variation_title})",
        themeColor: "#5096E8",
      },

      position: {
        "ui:widget": "radio",
        "ui:options": {
          inline: true,
        },
      },
      movement: {
        "ui:widget": "radio",
        "ui:options": {
          inline: true,
        },
      },

      duration_unit: {
        "ui:widget": "radio",
        "ui:options": {
          inline: true,
        },
      },
      equipment: {
        "ui:widget": "checkboxes",
        "ui:options": {
          inline: true,
        },
      },

      translations: {
        type: "object",
        "ui:options": {
          customObjectLayout: "horizontal",
          addLabel: "Add Translation",
          orderable: !isApproved,
          addable: !isApproved,
          removable: !isApproved,
        },
        items: {
          type: "object",
          "ui:options": {
            collapsed: true,
            collapsible: true,
            titleFromField: "locale",
            themeColor: "#e8b550ff",
          },
          caption_file_url: {
            "ui:widget": "CaptionUrlWidget",
          },
          image_url: {
            "ui:widget": "ImageUrlWidget",
          },
          video: {
            "ui:field": "VideoField",
          },
        },
      },
    },
  },
  widgets: {
    uploader: {
      "ui:options": {
        title: "Upload your file here",
      },
    },
  },
});

const ExerciseEditorInner = ({
  initialFormData,
  onSubmit: onSubmitProp,
  onDirty,
  isSubmitting,
  additionalActions,
  submitButtonText,
}: {
  initialFormData?: any;
  onSubmit: (data: any) => void;
  onDirty?: (isDirty: boolean) => void;
  isSubmitting?: boolean;
  additionalActions?: React.ReactNode;
  submitButtonText?: string;
}) => {
  const { t } = useTranslation();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { setFormData } = useFormData();
  const isApproved = initialFormData?.state === "approved";
  const uiSchema = useMemo(() => {
    return getPostExerciseUISchema(isApproved, submitButtonText);
  }, [isApproved, submitButtonText]);

  // Initialize context with initial form data
  React.useEffect(() => {
    if (initialFormData) {
      setFormData(initialFormData);
    }
  }, [initialFormData, setFormData]);

  const onChange = (data: any) => {
    setFormData(data.formData);
    if (!isDirty) {
      setIsDirty(true);
      onDirty?.(true);
    }
  };

  const onSubmit = (data: any) => {
    if (initialFormData?.creator_id != null) {
      if (!window.confirm(t("creator.confirm_exercise"))) return;
    }
    onSubmitProp(data);
  };

  return (
    <>
      {initialFormData && (
        <Box mb={2}>
          <Typography variant="h6">{t("creator.title")}</Typography>
          <Typography variant="body1">
            <CreatorLabel creatorId={initialFormData.creator_id} />
          </Typography>
        </Box>
      )}
      <GenericForm
        formData={initialFormData}
        schema={postExerciseSchema}
        uiSchema={uiSchema}
        onSubmit={onSubmit}
        onChange={onChange}
        isSubmitting={isSubmitting}
        additionalActions={additionalActions}
      />
    </>
  );
};

export const ExerciseEditor = (props: {
  initialFormData?: any;
  onSubmit: (data: any) => void;
  onDirty?: (isDirty: boolean) => void;
  isSubmitting?: boolean;
  additionalActions?: React.ReactNode;
  submitButtonText?: string;
}) => {
  return (
    <FormDataProvider>
      <ExerciseEditorInner {...props} />
    </FormDataProvider>
  );
};

export default ExerciseEditor;
